import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Chip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  AttachMoney as AttachMoneyIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import costService from '../services/cost.service';
import { CostType, COST_TYPE_LABELS, COST_CATEGORY_LABELS } from '../types/cost.types';
import { useSnackbar } from '../contexts/SnackbarContext';
import AuthLayout from '../components/AuthLayout';

const CostDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showSnackbar } = useSnackbar();
  
  const [cost, setCost] = useState<CostType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Estado para diálogo de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  
  // Carregar detalhes do custo
  useEffect(() => {
    const loadCost = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const costData = await costService.getCostById(id);
        setCost(costData);
      } catch (error) {
        console.error('Erro ao carregar custo:', error);
        showSnackbar('Erro ao carregar detalhes do custo. Tente novamente.', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadCost();
  }, [id, showSnackbar]);
  
  // Manipuladores para exclusão
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteCost = async () => {
    if (!id) return;
    
    try {
      await costService.deleteCost(id);
      showSnackbar('Custo excluído com sucesso!', 'success');
      navigate('/costs');
    } catch (error) {
      console.error('Erro ao excluir custo:', error);
      showSnackbar('Erro ao excluir custo. Tente novamente.', 'error');
    } finally {
      handleCloseDeleteDialog();
    }
  };
  
  // Renderizar informações específicas com base no tipo de custo e categoria
  const renderSpecificInfo = () => {
    if (!cost) return null;
    
    // Campos para mão de obra (técnica ou comercial)
    if (cost.category === 'technical_labor' || cost.category === 'commercial_labor') {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Detalhes de Mão de Obra
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Taxa Horária
              </Typography>
              <Typography variant="body1">
                {cost.hourlyRate ? costService.formatCurrency(cost.hourlyRate) : 'Não informado'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Horas Trabalhadas
              </Typography>
              <Typography variant="body1">
                {cost.hoursWorked ? `${cost.hoursWorked} horas` : 'Não informado'}
              </Typography>
            </Grid>
            {cost.hourlyRate && cost.hoursWorked && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Cálculo
                </Typography>
                <Typography variant="body1">
                  {costService.formatCurrency(cost.hourlyRate)} × {cost.hoursWorked} horas = {costService.formatCurrency(cost.amount)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      );
    }
    
    // Campos para deslocamento
    if (cost.category === 'transportation' || cost.category === 'visit_transportation') {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Detalhes de Transporte
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Quilômetros
              </Typography>
              <Typography variant="body1">
                {cost.kilometers ? `${cost.kilometers} km` : 'Não informado'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Custo por Km
              </Typography>
              <Typography variant="body1">
                {cost.costPerKilometer ? costService.formatCurrency(cost.costPerKilometer) : 'Não informado'}
              </Typography>
            </Grid>
            {cost.kilometers && cost.costPerKilometer && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Cálculo
                </Typography>
                <Typography variant="body1">
                  {cost.kilometers} km × {costService.formatCurrency(cost.costPerKilometer)} = {costService.formatCurrency(cost.amount)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      );
    }
    
    // Campos para materiais, equipamentos, etc.
    if (
      cost.category === 'parts_materials' || 
      cost.category === 'special_equipment' || 
      cost.category === 'demo_materials' || 
      cost.category === 'presentation_tools'
    ) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Detalhes de Materiais/Equipamentos
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Quantidade
              </Typography>
              <Typography variant="body1">
                {cost.quantity ? cost.quantity : 'Não informado'}
              </Typography>
            </Grid>
            {cost.quantity && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Valor Unitário
                </Typography>
                <Typography variant="body1">
                  {costService.formatCurrency(cost.amount / cost.quantity)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      );
    }
    
    return null;
  };
  
  // Renderizar informações de referência
  const renderReferenceInfo = () => {
    if (!cost) return null;
    
    if (cost.serviceRequest) {
      return (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Chamado Técnico Relacionado
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem 
                button 
                // @ts-ignore
                onClick={() => navigate(`/service-requests/${typeof cost.serviceRequest === 'string' ? cost.serviceRequest : cost.serviceRequest._id}`)}
              >
                <ListItemText
                  primary={typeof cost.serviceRequest !== 'string' ? cost.serviceRequest.number : 'Chamado'}
                  // @ts-ignore
                  secondary={typeof cost.serviceRequest !== 'string' ? cost.serviceRequest.title : 'Ver detalhes do chamado'}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      );
    }
    
    if (cost.commercialVisit) {
      return (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Visita Comercial Relacionada
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem 
                button 
                // @ts-ignore
                onClick={() => navigate(`/commercial-visits/${typeof cost.commercialVisit === 'string' ? cost.commercialVisit : cost.commercialVisit._id}`)}
              >
                <ListItemText
                  primary={typeof cost.commercialVisit !== 'string' ? cost.commercialVisit.number : 'Visita'}
                  secondary={typeof cost.commercialVisit !== 'string' ? cost.commercialVisit.description : 'Ver detalhes da visita'}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      );
    }
    
    return null;
  };
  
  if (loading) {
    return (
      <AuthLayout title="Carregando Custo...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }
  
  if (!cost) {
    return (
      <AuthLayout title="Custo Não Encontrado">
        <Box>
          <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
            Custo não encontrado
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/costs')}
          >
            Voltar para a lista
          </Button>
        </Box>
      </AuthLayout>
    );
  }
  
  return (
    <AuthLayout title="Detalhes do Custo">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Detalhes do Custo
        </Typography>
        
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/costs')}
            sx={{ mr: 1 }}
          >
            Voltar
          </Button>
          
          <Tooltip title="Editar">
            <IconButton 
              color="primary"
              onClick={() => navigate(`/costs/${cost._id}/edit`)}
              sx={{ mr: 1 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Excluir">
            <IconButton 
              color="error"
              onClick={handleOpenDeleteDialog}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ReceiptIcon color="primary" sx={{ mr: 2 }} />
              <Typography variant="h6">
                Informações Gerais
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DescriptionIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Descrição
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                  {cost.description}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Tipo de Custo
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Chip 
                    label={COST_TYPE_LABELS[cost.costType as keyof typeof COST_TYPE_LABELS]} 
                    color={
                      cost.costType === 'technical_direct' ? 'primary' :
                      cost.costType === 'commercial_direct' ? 'secondary' : 'default'
                    }
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Categoria
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                  {COST_CATEGORY_LABELS[cost.category]}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DateRangeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Data
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                  {new Date(cost.date).toLocaleDateString('pt-BR')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Valor
                  </Typography>
                </Box>
                <Typography variant="h6" color="primary" sx={{ ml: 4 }}>
                  {costService.formatCurrency(cost.amount)}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Registrado por
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                  {typeof cost.registeredBy !== 'string' ? (cost.registeredBy?.displayName || 'Usuário não disponível') : 'Usuário não disponível'}
                </Typography>
              </Grid>
              
              {cost.notes && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NotesIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1">
                      Observações
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    {cost.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
            
            {renderSpecificInfo()}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          {renderReferenceInfo()}
          
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações do Sistema
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="ID" 
                    secondary={cost._id} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Criado em" 
                    secondary={new Date(cost.createdAt).toLocaleString('pt-BR')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Última atualização" 
                    secondary={new Date(cost.updatedAt).toLocaleString('pt-BR')} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este custo? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteCost} color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default CostDetail;
